import { Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EChartOption, SiChartComponent } from '@siemens/charts-ng';
import { SiEmptyStateComponent } from '@siemens/element-ng/empty-state';

import { LatLng } from '../../models/brouter-map.model';
import { cumulativeDistancesKm } from '../../services/track-geometry';

/**
 * Elevation profile along the track.
 *
 * Replaces the legacy `plugin/Heightgraph.js` (d3) with the project's charting component.
 * BRouter returns elevation as the third coordinate component, so tracks routed over
 * areas without elevation data simply have none.
 */
@Component({
  selector: 'app-elevation-chart',
  imports: [TranslateModule, SiChartComponent, SiEmptyStateComponent],
  templateUrl: './elevation-chart.component.html',
  styleUrl: './elevation-chart.component.scss'
})
export class ElevationChartComponent {
  /** Track geometry, ordered from start to destination. */
  readonly track = input.required<readonly LatLng[]>();
  /** Elevation per track point, in metres. Same length and order as `track`. */
  readonly elevations = input.required<readonly (number | null)[]>();

  /** Emits the track position under the pointer, or `null` when it leaves the chart. */
  readonly pointHovered = output<LatLng | null>();
  readonly closed = output<void>();

  private readonly translateService = inject(TranslateService);

  readonly hasElevationData = computed(() => this.elevations().some(value => value !== null));

  readonly chartOptions = computed<EChartOption | null>(() => {
    const elevations = this.elevations();
    const track = this.track();

    if (!this.hasElevationData() || track.length < 2) {
      return null;
    }

    const distances = cumulativeDistancesKm(track);
    const data = elevations.map((elevation, i) => [distances[i], elevation]);

    return {
      grid: { top: 16, right: 16, bottom: 28, left: 48 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: number) => `${Math.round(value)} m`
      },
      xAxis: {
        type: 'value',
        name: this.translateService.instant('BROUTER_MAP.ELEVATION.DISTANCE'),
        nameLocation: 'middle',
        nameGap: 22,
        // Otherwise echarts rounds the axis past the last point and leaves a gap.
        min: 'dataMin',
        max: 'dataMax',
        axisLabel: { formatter: '{value} km' }
      },
      yAxis: {
        type: 'value',
        name: this.translateService.instant('BROUTER_MAP.ELEVATION.ALTITUDE'),
        axisLabel: { formatter: '{value} m' },
        scale: true
      },
      series: [
        {
          type: 'line',
          name: this.translateService.instant('BROUTER_MAP.ELEVATION.ALTITUDE'),
          showSymbol: false,
          smooth: true,
          areaStyle: {},
          data
        }
      ]
    } as EChartOption;
  });

  /**
   * The chart is indexed by track point, so a hovered data point maps straight back to a
   * coordinate without another distance lookup.
   */
  onChartHover(event: { dataIndex?: number }): void {
    const index = event?.dataIndex;
    const point = index !== undefined ? this.track()[index] : undefined;
    this.pointHovered.emit(point ?? null);
  }

  onMouseLeave(): void {
    this.pointHovered.emit(null);
  }
}
